import { resolveAppointmentOutcomeSelection } from '../../../utils/appointmentOutcomeSelection';
import { buildAIBookingPaymentPayload } from '../../../utils/aiBookingPayment';
// ────────────────────────────────────────────────────────────────────────────────
// Appointment Thunks
// ────────────────────────────────────────────────────────────────────────────────
// All appointment-related async operations: CRUD, attendance, follow-up handling

import { dispatch, store } from '../../store';
import { updateError } from '../user';
import { callApi } from '../../../utils/apiCall';
import { captureAppointmentAttendance, captureConsultationAttendance, attendanceResponse, isSeparatelyRequestedFutureTreatmentBooking } from '../../../utils/attendanceActions.ts';
import { attendanceCoordinator } from '../../../hooks/useAttendanceCapture.ts';
import { invalidateAttendance } from '../attendanceReview.ts';
import { cancelAppointmentWithDentally, rescheduleAppointmentWithDentally } from '../../../utils/dentallyApi';
import {
    appointmentDatetimeForSync,
    buildAppointmentSyncToast,
    resolveAppointmentSyncOutcome,
    shouldConfirmDentallySync,
} from '../../../utils/dentallyAppointmentSync';
import { errorHandler } from '../../../utils/errorHandler';
import { setLoader } from '../loader';
import { creteTreatmentPaymentFlow, setDepositData } from '../deposit';
import { getCurrentTreatmentIdV2 } from '../clincheck';
import { leadActions, startLoadingData, updateCompleteStatus } from './slice';

import { CompleteTreatment, StartingTreatment, UpdateTreatmentV2 } from './treatmentThunks';
import { addNotes, updatePersonalDetail, getCurrentLeadAppointmentId } from './coreThunks';
import { leadTreatmentPayment } from './financialThunks';
import { addReminder, updateAssignToUser } from './taskThunks';
import { withAssigneeIds } from '../../../utils/toAssigneeIds';
import { isAppointmentDateLocked, appointmentDateLockMessage } from '../../../utils/appointmentDateLock';
import { attendanceUiEnabledFor } from '../../../utils/attendanceUiFlag';

const BOOKED_APPOINTMENT_STATUSES = ['BOOKED', 'CONSULTATION_BOOKED'];
const ATTENDED_APPOINTMENT_STATUSES = ['ATTENDED', 'CONSULTATION_ATTENDED'];
const MAX_CANCELLATION_POLL_ATTEMPTS = 3;
const MAX_CANCELLATION_POLL_DELAY_MS = 30000;
const DEFAULT_CANCELLATION_POLL_DELAY_MS = 1000;
let latestUnscopedAppointmentRequestId = 0;

// ── Double-submit guard for bookings ────────────────────────────────────────
// The Book / Finish buttons do not disable while their POST is in flight. The
// request takes 1-4s and shows no spinner, callApi only de-dupes idempotent
// methods (GET|HEAD|OPTIONS), and EVERY booking surface in the app — follow-up
// V2, the AI call script, NewAppointment, LeadActionZone, BookedWithField, the
// lead tables — funnels through addAppointment() below. So a second click sent
// a second create. Production: 174 duplicate appointment pairs across 18 of 36
// practices, one slot with 15 copies, and patients who received the same
// booking confirmation twice.
//
// The backend now returns the existing appointment rather than creating
// another (AppointmentViewset.create), so this is defence in depth — but it is
// the layer that stops the request being sent at all, and because it sits in
// the one shared thunk it covers every caller, including ones added later.
//
// Keyed on the BOOKING, not on the click: the same slot for the same treatment
// is one booking however many times it is submitted. A payload carrying no
// treatment or no datetime is left unguarded, because there is nothing to key
// it on.
const inFlightBookings = new Set();

const bookingKey = (practiceId, leadId, data) => {
    if (!data?.lead_treatment || !data?.datetime) return null;
    return [practiceId, leadId, data.lead_treatment, data.datetime, data.type || ''].join('|');
};

// bookingInFlight is one boolean for the whole app, but two DIFFERENT bookings
// can overlap — book on one lead, navigate, book on another before the first
// response lands. Lowering the flag on the first settlement would stop the
// second booking's spinner while it is still running. Counting instead means
// the flag is raised by the first booking to start and lowered by the last to
// finish. Unkeyed payloads are counted too: they are not de-duplicated, but
// they are still a request the user is waiting on.
let bookingsInFlight = 0;

const raiseBookingInFlight = () => {
    bookingsInFlight += 1;
    if (bookingsInFlight === 1) dispatch(leadActions.setBookingInFlight(true));
};

const lowerBookingInFlight = () => {
    bookingsInFlight = Math.max(0, bookingsInFlight - 1);
    if (bookingsInFlight === 0) dispatch(leadActions.setBookingInFlight(false));
};

// String-safe id equality — treatment ids arrive as numbers (API payloads) AND as
// strings (row data / URL params). Strict === silently mismatches ("112852" vs 112852),
// which re-targets multi-treatment actions at the wrong (e.g. completed) row.
const idEq = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

const captureAppointmentContext = (requestScopeRef, practiceId, leadId) => {
    if (!requestScopeRef) return null;
    return {
        generation: requestScopeRef.current?.generation,
        practiceId: String(practiceId),
        leadId: String(leadId),
    };
};

const isAppointmentContextCurrent = (requestScopeRef, capturedScope) => {
    if (!requestScopeRef) return true;
    const { current } = requestScopeRef;
    return Boolean(
        current?.active
        && current.generation === capturedScope?.generation
        && current.practiceId === capturedScope?.practiceId
        && current.leadId === capturedScope?.leadId
    );
};

const captureAppointmentRead = (requestScopeRef, practiceId, leadId) => {
    if (!requestScopeRef) {
        latestUnscopedAppointmentRequestId += 1;
        return { unscopedRequestId: latestUnscopedAppointmentRequestId };
    }
    const context = captureAppointmentContext(requestScopeRef, practiceId, leadId);
    const requestId = (requestScopeRef.current?.latestRequestId || 0) + 1;
    requestScopeRef.current.latestRequestId = requestId;
    return { ...context, requestId };
};

const isAppointmentReadCurrent = (requestScopeRef, capturedRead) => {
    if (!requestScopeRef) {
        return capturedRead?.unscopedRequestId === latestUnscopedAppointmentRequestId;
    }
    return isAppointmentContextCurrent(requestScopeRef, capturedRead)
        && requestScopeRef.current?.latestRequestId === capturedRead?.requestId;
};

// Legacy appointment editors do not own a React request scope. Capture their
// navigation identity so a late save cannot fetch an old lead into shared Redux.
const createEditorReadScope = (practiceId, leadId) => {
    const identity = () => {
        const state = store.getState();
        return JSON.stringify([
            state.practiceTreatmentList?.practiceId,
            state.lead?.leadDetail?.id,
            state.lead?.followUpLead?.id,
            state.lead?.tableRowLead?.lead_id,
            typeof window === 'undefined' ? '' : window.location.pathname,
        ]);
    };
    const captured = identity();
    return { current: {
        get active() { return identity() === captured; },
        generation: 1, latestRequestId: 0,
        practiceId: String(practiceId), leadId: String(leadId),
    } };
};

const appointmentListUrl = (practiceId, leadId) => (
    // deposit_summary — AppointmentDepositBadge's data contract (Overview +
    // Treatments-tab appointment lists). The backend serializer always
    // includes it, but this sparse `fields=` whitelist strips anything not
    // explicitly named, so it was silently dropped and the badge never
    // rendered (2026-09-26 pre-prod QA finding on feat/payment-appointment-link).
    `/api/v1/practices/${practiceId}/leads/${leadId}/appointments/?fields={id,status,datetime,datetime_human,method,type,contacted_via,note,assignees,lead_treatment,lead_treatments,dentally,calcom,deposit_summary}`
);

const cancellationPollDelay = (retryAfterSeconds, attempt) => {
    const parsedSeconds = Number(retryAfterSeconds);
    const initialDelay = Number.isFinite(parsedSeconds) && parsedSeconds > 0
        ? Math.min(parsedSeconds * 1000, MAX_CANCELLATION_POLL_DELAY_MS)
        : DEFAULT_CANCELLATION_POLL_DELAY_MS;
    return Math.min(initialDelay * (2 ** attempt), MAX_CANCELLATION_POLL_DELAY_MS);
};

const waitForAppointmentScope = (delayMs, requestScopeRef, capturedContext) => {
    if (!requestScopeRef) {
        return new Promise((resolve) => setTimeout(() => resolve(true), delayMs));
    }
    if (!isAppointmentContextCurrent(requestScopeRef, capturedContext)) return Promise.resolve(false);

    const scope = requestScopeRef.current;
    if (!scope.cancelWaits) scope.cancelWaits = new Set();
    return new Promise((resolve) => {
        let settled = false;
        const finish = (isCurrent) => {
            if (settled) return;
            settled = true;
            scope.cancelWaits.delete(cancelWait);
            resolve(isCurrent);
        };
        const timer = setTimeout(
            () => finish(isAppointmentContextCurrent(requestScopeRef, capturedContext)),
            delayMs,
        );
        const cancelWait = () => {
            clearTimeout(timer);
            finish(false);
        };
        scope.cancelWaits.add(cancelWait);
    });
};

const getTreatmentAppointments = (appointments, treatmentId) => (
    appointments?.filter((appointment) => idEq(appointment?.lead_treatment, treatmentId)) || []
);

// Newest appointment for a treatment regardless of status. Legacy datetimes
// ("DD-MM-YYYY HH:mm:ss.0Z") do not parse with Date, so fall back to the id order.
const mostRecentAppointment = (appointments) => {
    const time = (a) => { const t = Date.parse(a?.datetime); return Number.isNaN(t) ? 0 : t; };
    return [...(appointments || [])].filter(Boolean).sort((a, b) => (time(b) - time(a)) || ((b?.id ?? 0) - (a?.id ?? 0)))[0] ?? null;
};

const clearAttendanceLoaders = () => {
    dispatch(updateCompleteStatus(false));
    dispatch(startLoadingData(false));
    dispatch(setLoader(false));
};

// A deposit intent (depositData.isDepositFlow) is only meaningful for the
// exact lead + treatment it was set for (NewAppointment.jsx's deposit
// checkbox). `depositData.leadId`/`treatmentId` are stamped there at set
// time — require both to be present and match this booking; a missing
// leadId (nothing should produce that going forward) is treated as
// unscoped/untrusted rather than silently matching everything.
const isDepositDataForThisBooking = (depositData, bookingLeadId, bookingTreatmentId) => (
    idEq(depositData?.leadId, bookingLeadId) && idEq(depositData?.treatmentId, bookingTreatmentId)
);

// A deposit intent left set for more than this long (popup opened, checkbox
// ticked, then abandoned without ever booking) is treated as stale even if
// a later booking happens to match the same lead/treatment.
const DEPOSIT_FLOW_MAX_AGE_MS = 30 * 60 * 1000;
const isDepositDataStale = (depositData) => (
    typeof depositData?.createdAt === 'number' && (Date.now() - depositData.createdAt) > DEPOSIT_FLOW_MAX_AGE_MS
);

// Single gate for every place in this file that can trigger the deposit
// checkbox's payment (NewAppointment.jsx's "Starting treatment" flow can
// resolve to a NEW appointment via addAppointment(), an ALREADY-ATTENDED
// appointment via ConsultationAttended(), or a booked-appointment-being-
// marked-attended via RecordAttended() — the popup itself doesn't know or
// care which path fires next, it just leaves isDepositFlow:true and lets
// whichever thunk runs check it). Reads the CURRENT depositData off the
// store (not a snapshot) so it reflects whatever the caller's own earlier
// reads captured. Only fires the payment when depositData was set for this
// exact lead + treatment and isn't stale; otherwise it's dropped silently
// and depositData is cleared so it can never fire against a later,
// unrelated booking either.
const maybeRunScopedDepositFlow = ({ leadId, treatmentId, appointmentId }) => {
    const { depositData } = store.getState().deposit;
    if (!depositData?.isDepositFlow) return;
    const scopedToThisBooking = isDepositDataForThisBooking(depositData, leadId, treatmentId);
    if (scopedToThisBooking && !isDepositDataStale(depositData)) {
        dispatch(setDepositData({
            ...depositData,
            ...(appointmentId != null ? { appointment: appointmentId } : {}),
        }));
        dispatch(creteTreatmentPaymentFlow());
    } else {
        dispatch(setDepositData(null));
    }
};

export function addAppointment(data, practiceId, id, handleClose, status, onSettled, isScopeCurrent, bookingCompletion) {
    return async () => {
        const canApply = () => (!isScopeCurrent || isScopeCurrent()) && (!bookingCompletion || bookingCompletion.isScopeCurrent());
        if (bookingCompletion && (!canApply() || String(practiceId) !== String(bookingCompletion.practiceId) || String(id) !== String(bookingCompletion.leadId) || String(data?.lead_treatment) !== String(bookingCompletion.treatmentId))) return;
        const key = bookingKey(practiceId, id, data);
        if (key !== null && inFlightBookings.has(key)) {
            // The first submission of this exact booking is still running and
            // owns the spinner, the success toast and handleClose. Nothing here
            // touches those — clearing them would tear down the UI state the
            // in-flight request is about to resolve.
            //
            // But the click is NOT swallowed in silence: a user who clicked
            // again did so because the screen looked frozen, and answering with
            // nothing at all teaches them to keep clicking. severity 'warning'
            // routes this through errorHandler.jsx as a warning toast, which is
            // also the variant that skips Sentry capture — this is expected
            // behaviour, not an error.
            dispatch(updateError({
                success: 'warning',
                severity: 'warning',
                message: 'Still saving this booking — no need to click again.',
            }));
            return;
        }
        if (!bookingCompletion && store.getState().lead?.action === 'startTreatment') {
            if (!data?.lead_treatment) {
                dispatch(updateError({ success: false, message: 'Select the exact treatment before starting.' }));
                return;
            }
            if (key !== null) inFlightBookings.add(key);
            const details = { ...(store.getState().lead.newLeadDetail?.lead_treatment || {}), lead: id, id: data.lead_treatment };
            try {
                await dispatch(StartingTreatment(details, practiceId, async () => {
                    // Attendance/start succeeded. The requested future treatment
                    // booking is a separate follow-on; a failure must not replay start.
                    dispatch(leadActions.setAction(null));
                    if (key !== null) inFlightBookings.delete(key);
                    if (isSeparatelyRequestedFutureTreatmentBooking(data)) return dispatch(addAppointment(data, practiceId, id, handleClose, status, onSettled, isScopeCurrent));
                    handleClose?.('finish', id); onSettled?.();
                    return undefined;
                }, 'startingTreatmentFinish'));
            } finally { if (key !== null) inFlightBookings.delete(key); }
            return;
        }
        if (key !== null) inFlightBookings.add(key);
        // Drives the Finish button's spinner and disabled state. Raised here
        // rather than in each screen so every booking surface gets it, and
        // lowered in the finally below so it can never strand the button.
        raiseBookingInFlight();
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/appointments/`, 'post', withAssigneeIds(data));
            // Once this explicit booking is accepted, its captured received
            // payment remains authorized even if the user navigates away.
            // Scope only controls UI updates after that persistence boundary.
            if (!canApply() && !(bookingCompletion && response?.status === 201)) return;
            if (response?.status === 201) {
                if (canApply()) dispatch(leadActions.saveAppointment(response.data));
                if (bookingCompletion) {
                    const context = bookingCompletion;
                    try {
                        if (context.personalDetail) {
                            const personal = await callApi(`/api/v1/practices/${context.practiceId}/leads/${context.leadId}/`, 'patch', { ...context.personalDetail, id: context.leadId });
                            if (personal?.status !== 200) throw new Error('personal details');
                        }
                        const payment = await callApi(`/api/v1/practices/${context.practiceId}/leads/${context.leadId}/treatments/${context.treatmentId}/payments/`, 'post', { ...context.payment, appointment: response.data.id });
                        if (payment?.status !== 201) throw new Error('received payment');
                        if (!canApply()) return;
                        dispatch(updateError({ success: true, message: 'Appointment booked and payment recorded successfully' }));
                    } catch (error) {
                        // A 201 already booked the visit. Never invite another
                        // booking or retry an ambiguously accepted payment.
                        dispatch(updateError({ success: 'warning', severity: 'warning', message: `Appointment booked (appointment ${response.data.id}, lead ${context.leadId}, practice ${context.practiceId}), but personal details or received payment could not be confirmed. Do not book again. Check this appointment and its payments before recording anything again.` }));
                    }
                    if (!canApply()) return;
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    dispatch(setLoader(false));
                    handleClose?.('finish', context.leadId);
                    return;
                }
                const { activeTab, newLeadDetail, attendance, createLead, tableRowLead, action } = store.getState().lead;
                // See maybeRunScopedDepositFlow's own comment — this addAppointment()
                // runs for EVERY booking surface (AI call-script wizard, follow-up
                // booking forms, Dentally wizard, NewAppointment.jsx itself), so an
                // unscoped check here would fire a stale deposit payment against
                // whatever unrelated lead/treatment is booked next.
                maybeRunScopedDepositFlow({ leadId: id, treatmentId: data?.lead_treatment, appointmentId: response.data.id });
                if (status === 'treatmentStartedAppointment') {
                    // Called only from StartingTreatment()'s follow-on appointment
                    // creation, AFTER the treatment_started/ POST already succeeded
                    // and already reported its own "Treatment started successfully"
                    // toast — don't duplicate that message here, just close/refresh.
                    dispatch(startLoadingData(false));
                    return handleClose('finish', id);
                }
                if (status && status?.toLowerCase?.() === 'finish') {
                    dispatch(updateError({ success: true, message: 'Treatment started successfully' }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', id);
                    dispatch(startLoadingData(false));
                    return;
                }

                const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
                if (action === 'completeTreatment') {
                    const completeTreatment = {
                        ...newLeadDetail?.lead_treatment
                    };
                    dispatch(CompleteTreatment(completeTreatment, data?.lead || newLeadDetail?.personalDetail?.id, currentTreatmentDataGlobal?.treatmentData?.id || newLeadDetail?.lead_treatment?.id || tableRowLead?.treatment_id, practiceId, handleClose));
                    return;
                }
                if (attendance === 'attendance' && activeTab === 2) {
                    const { noteDetail } = newLeadDetail?.attentendAppoiment || {};
                    if (noteDetail) dispatch(addAppointmentNotes(practiceId, id, response.data.id, noteDetail, handleClose));
                    return;
                }
                if (attendance === 'payment' && (status && status === 'followUp') && activeTab >= 2) {
                    dispatch(updatePersonalDetail({ ...newLeadDetail.personalDetail, id: tableRowLead.lead_id ?? tableRowLead.id }, practiceId, handleClose, 'personalDetail'));
                    return;
                }

                if (attendance === 'payment' && (status && status === 'CreatLead') && activeTab >= 2 && newLeadDetail.payment.payment_type !== 'No') {
                    // Guard: both leadId and treatmentId are embedded directly in the
                    // payment API URL — passing undefined would produce a broken request.
                    if (!createLead?.lead_treatment?.id) {
                        dispatch(updateError({ success: false, message: 'No treatment selected for this lead' }));
                        return;
                    }
                    // Contract: Payment create accepts an optional `appointment` id —
                    // the appointment just created above is the one this payment is
                    // for.
                    dispatch(leadTreatmentPayment(practiceId, createLead?.lead_treatment?.lead, createLead?.lead_treatment?.id, { ...newLeadDetail.payment, appointment: response.data.id }, handleClose));
                    return;
                }

                // if (attendance === 'payment' && activeTab >= 2) {
                // 	// const reminderDetail = {
                // 	// 	...newLeadDetail.lead_reminder,
                // 	// 	lead : createLead.id,
                // 	// 	lead_treatment : createLead.lead_treatment ? createLead.lead_treatment.id : tableRowLead.treatment_id,
                // 	// 	lead_appointment : createLead.lead_appointment ? createLead.lead_treatment.id : 0
                // 	// }
                // 	// dispatch(addReminder(reminderDetail, practiceId, handleClose));
                // 	dispatch(updateError({ success: true, message: 'Treatment and Appointment Added successfully' }));
                // 	dispatch(updateCompleteStatus(false));
                // 	handleClose('finish');
                // 	return;
                // }
                if (status === 'RescheduleReminder') {
                    dispatch(updateError({ success: true, message: 'Appointment added successfully' }));
                    dispatch(getAppointment(practiceId, id));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', id);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'AddAppointment') {
                    dispatch(updateError({ success: true, message: 'Appointment added successfully' }));
                    dispatch(getAppointment(practiceId, id));
                    dispatch(followUpSlice(id, practiceId, 'LeadDetail'));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', id);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'Reschedule') {
                    dispatch(updateError({ success: true, message: 'Appointment added successfully' }));
                } else {
                    dispatch(updateError({ success: true, message: 'Treatment and Appointment added successfully' }));
                }
                dispatch(updateCompleteStatus(false));
                handleClose('finish', id);
                dispatch(startLoadingData(false));

            } else {
                dispatch(updateCompleteStatus(false));
                if (status === 'treatmentStartedAppointment') {
                    // The treatment itself already started successfully — this is
                    // only the follow-on appointment failing. Say so explicitly
                    // (not the generic appointment-failure message) and still
                    // close/refresh: leaving the dialog open here would invite the
                    // user to press Finish again, which would re-POST
                    // treatment_started/ for a treatment that already started.
                    dispatch(updateError({ message: "Treatment started, but the treatment appointment couldn't be created", success: false }));
                    dispatch(startLoadingData(false));
                    dispatch(setLoader(false));
                    return handleClose('finish', id);
                }
                const errMsg = response.data && typeof response.data === 'object'
                    ? Object.values(response.data).flat().join(', ')
                    : 'Failed to save appointment. Please check the form and try again.';
                dispatch(updateError({ message: errMsg, success: false }));
                dispatch(startLoadingData(false));
                dispatch(setLoader(false));
            }
        } catch (error) {
            if (!canApply()) return;
            dispatch(setLoader(false));
            if (status === 'treatmentStartedAppointment') {
                dispatch(updateError({ message: "Treatment started, but the treatment appointment couldn't be created", success: false, cause: error }));
                dispatch(startLoadingData(false));
                return handleClose('finish', id);
            }
            dispatch(updateError({ message: 'Failed to save appointment. Please try again.', success: false, cause: error }));
        } finally {
            if (key !== null) inFlightBookings.delete(key);
            // Unconditional, and paired with the key release: the spinner and the
            // guard are released by the same statement pair on every exit path —
            // success, validation failure, network error and scope-cancelled
            // alike — so the button cannot be left spinning or blocked.
            lowerBookingInFlight();
            if (canApply()) onSettled?.();
        }
    };
}

// Called only from the AI Booking Wizard's success path (CallScriptV2's
// handleAIBookAppointment), after addAppointment() has already confirmed the
// appointment (201). The wizard collects payment_option/payment_amount/
// payment_method itself but previously never turned that into a payment record —
// this reuses leadTreatmentPayment (the SAME thunk the standard booking flow
// uses) so a deposit/consultation fee taken here behaves identically
// (paid_at set server-side for received modes, Stripe checkout created
// atomically for 'SEND'). The appointment is never rolled back if this fails —
// leadTreatmentPayment already surfaces the backend error via updateError.
export function saveAIBookingPayment(practiceId, leadId, appointmentData, leadContact, handleClose = () => {}) {
    return async () => {
        const result = buildAIBookingPaymentPayload(appointmentData, leadContact);
        if (!result) return;
        if (result.error) {
            dispatch(updateError({ success: false, message: result.error }));
            return;
        }
        dispatch(leadTreatmentPayment(practiceId, leadId, appointmentData?.lead_treatment, result.payload, handleClose));
    };
}

export function getAppointment(practiceId, id, requestScopeRef = null) {
    return async () => {
        const capturedRead = captureAppointmentRead(requestScopeRef, practiceId, id);
        try {
            const response = await callApi(appointmentListUrl(practiceId, id));
            if (!isAppointmentReadCurrent(requestScopeRef, capturedRead)) return null;
            if (response?.status === 200) {
                dispatch(leadActions.followUpAppointment(response.data.results));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
            }
            return response;
        } catch (error) {
            if (!isAppointmentReadCurrent(requestScopeRef, capturedRead)) return null;
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            return {
                ok: false,
                status: error?.response?.status || error?.status || 0,
                error,
            };
        }
    };
}

// `options.dentally` is the appointment's DentallyAppointmentLink.safe_summary()
// as the calling dialog already has it. Supplying it opts this edit into the
// durable reschedule confirmation below. Omitting it — which every pre-existing
// caller does — keeps the single-request CRM-only behaviour unchanged.
export function UpdateAppointment(data, practiceId, handleNext, status, options = {}) {
    return async () => {
        const requestScope = createEditorReadScope(practiceId, data?.lead);
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/appointments/${data.id}/`, 'put', withAssigneeIds(data));
            if (!requestScope.current.active) return;
            if (response?.status === 202 && response?.data?.calcom) {
                dispatch(getAppointment(practiceId, data?.lead, requestScope));
                dispatch(updateError({ severity: 'warning', message: 'Cal.com change queued. The appointment updates after confirmation.' }));
            } else if (response?.status === 200) {
                // The CRM write above has already succeeded. So a failed
                // provider write must not read as "nothing saved", and a
                // success toast must not imply Dentally moved when it did not.
                //
                // The PUT saves the CRM and stops; it makes no provider call.
                // This second request is the ONE AND ONLY write path to
                // Dentally for an edit, which is why it fires whether or not
                // anything intends to show its result. A CRM-only appointment
                // never reaches it.
                const linkSummary = options.dentally || response.data?.dentally || null;
                const willConfirm = shouldConfirmDentallySync(linkSummary);
                // Persisting the PUT body now would store a `dentally` block
                // that says PENDING / unsynced — true for the instant between
                // the two calls, and about to be resolved by the second. Saving
                // it would flash "Dentally not updated" on an edit that is
                // succeeding, so the confirm path saves the authoritative state
                // instead, once it has it.
                if (!willConfirm) dispatch(leadActions.saveAppointment(response.data));

                let syncOutcome = null;
                if (willConfirm) {
                    // The SAME string the PUT just sent, never a reformatted
                    // one: the two requests must describe one instant, and the
                    // legacy and ISO spellings of these digits are an hour
                    // apart through British Summer Time.
                    const syncDatetime = appointmentDatetimeForSync(data?.datetime);
                    const syncResponse = syncDatetime
                        ? await rescheduleAppointmentWithDentally(practiceId, data?.lead, data.id, {
                            datetime: syncDatetime,
                            expectedDentallyAppointmentId: linkSummary.appointment_id,
                        })
                        : null;
                    if (!requestScope.current.active) return;
                    syncOutcome = resolveAppointmentSyncOutcome(syncResponse);
                    // When the provider write did not land, the PUT body's
                    // PENDING block is no longer transient — it is the truth,
                    // and the row badge should say so.
                    dispatch(leadActions.saveAppointment(
                        syncOutcome?.dentally
                            ? { ...response.data, dentally: syncOutcome.dentally }
                            : response.data,
                    ));
                }
                const syncToast = buildAppointmentSyncToast(syncOutcome);
                const successToast = syncToast
                    || { success: true, message: 'Appointment updated successfully' };
                if (status === 'RescheduleReminder') {
                    handleNext?.('finish', data?.lead);
                    dispatch(getAppointment(practiceId, data?.lead, requestScope));
                    dispatch(updateError(successToast));
                }
                if (status === 'Reschedule') {
                    handleNext?.('finish', data?.lead);
                    dispatch(updateError(successToast));
                }
                // Refresh so the appointment's Dentally badge stops claiming a
                // state the provider write did not reach.
                if (syncOutcome && status !== 'RescheduleReminder') {
                    dispatch(getAppointment(practiceId, data?.lead, requestScope));
                }
                handleNext?.({
                    success: true,
                    message: successToast.message,
                    dentallySync: syncOutcome || null,
                });
            } else if (isAppointmentDateLocked(response)) {
                // §5 date lock: nothing was saved; keep the dialog's state and say why.
                dispatch(updateError({ success: false, message: appointmentDateLockMessage(response) }));
                handleNext?.({ success: false, dateLocked: true, message: appointmentDateLockMessage(response) });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || 'Could not confirm the appointment update. Refresh the current details.' }));
                dispatch(getAppointment(practiceId, data?.lead, requestScope));
            }
            dispatch(startLoadingData(false));
        } catch (error) {
            if (!requestScope.current.active) return;
            const message = error?.message || 'An error occurred while updating the appointment';
            dispatch(updateError({ success: false, message }));
            dispatch(leadActions.hasError(message));
        }
    };
}

export function FailedToAttendAppointment(data, leadId, id, practiceId, handleClose, activeTab) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${id}/failed_to_attend/`, 'post', data);
            const { newLeadDetail: { archiveLead }, tableRowLead } = store.getState().lead;
            if (response?.status === 200) {
                if (activeTab === 'failedAttendTreatment') {
                    handleClose({ success: true });
                    dispatch(startLoadingData(false));
                    return;
                }
                dispatch(updateError({ success: true, message: 'Appointment failed successfully' }));
                if (activeTab === 'Unable to contact') {
                    dispatch(unableToContact(handleClose));
                    return;
                }
                if (activeTab === 'Archive') {
                    const archiveNote = {
                        ...archiveLead.notes,
                        lead: tableRowLead.lead_id
                    };
                    dispatch(addNotes(archiveNote, practiceId, handleClose));
                    return;
                }
                if (activeTab === 'Reschedule') {
                    dispatch(Reschedule(handleClose));
                }
            } else {
                errorHandler(response.data, handleClose, leadActions.hasError);
            }
        } catch (error) {
            errorHandler(error, handleClose, leadActions.hasError);
        }
    };
}

export function followUpSlice(id, practiceId, LeadDetail, handleClose) {
    return async () => {
        try {
            // Slim payload: omits heavy form_submissions[*].fields[*]. Only ScriptListing
            // needs the full fields, and it lazy-fetches them via the dedicated endpoint.
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/?slim=true`);
            if (response?.status === 200) {
                if (handleClose) {
                    handleClose(response.data);
                }
                if (LeadDetail === 'LeadDetail') {
                    dispatch(leadActions.saveLeadDetail(response.data));
                    dispatch(leadActions.followUpLead(response.data));
                    return;
                }
                dispatch(leadActions.followUpLead(response.data));
                const newCreateLead = {
                    id: response.data.id,
                    first_name: response.data.first_name,
                    last_name: response.data.last_name,
                    salutation: response.data.salutation,
                    dob: response.data.dob,
                    address: response.data?.lead_address === null ? '' : response.data?.lead_address,
                    lead_contact: {
                        email: response.data?.lead_contact.email,
                        note: response.data?.lead_contact.note,
                        phone: response.data?.lead_contact.phone,
                        preferred_contact_method: response.data?.lead_contact.preferred_contact_method,
                    }
                };
                dispatch(leadActions.saveLead(newCreateLead));
            } else if (response?.status === 404) {
                if (LeadDetail === 'LeadDetail') {
                    dispatch(leadActions.saveLeadDetail(null));
                    dispatch(leadActions.setLeadDetailNotFound(true));
                } else {
                    dispatch(leadActions.setFollowLeadNotFound(true));
                }
            } else {
                if (LeadDetail === 'LeadDetail') {
                    dispatch(leadActions.saveLeadDetail(null));
                }
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error?.message));
        }
    };
}

export function followUpAppointmentHandler(appointmentData) {
    return dispatch(leadActions.isAppointment(appointmentData));
}

export function appointmentsAttended(practiceId, leadId, id, data, handleClose) {
    return async () => {
        try {
            const outcome = await captureAppointmentAttendance({ practiceId, leadId, appointmentId: id, legacyData: data });
            if (outcome.status !== 'success') {
                dispatch(updateCompleteStatus(false)); dispatch(startLoadingData(false)); dispatch(setLoader(false));
                return;
            }
            const response = outcome.data;
            const returnedAppointment = response?.data;
            const treatmentId = returnedAppointment?.lead_treatment?.id ?? returnedAppointment?.lead_treatment;
            dispatch(invalidateAttendance({ practiceId, leadId, treatmentId, appointmentId: id }));
            if (returnedAppointment?.id && !idEq(returnedAppointment.id, id)) dispatch(invalidateAttendance({ practiceId, leadId, treatmentId, appointmentId: returnedAppointment.id }));
            if (response?.status === 200) {
                handleClose({ success: true, appointment: returnedAppointment });
                dispatch(startLoadingData(false));
                return;
            }
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
            dispatch(updateError({ message: JSON.stringify(response.data || 'Something went wrong'), success: false }));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
            dispatch(updateError({ message: JSON.stringify(error.message || 'Something went wrong'), success: false, cause: error }));
        }
    };
}

export function addAppointmentNotes(practiceId, leadId, id, data, handleClose, attendanceStarted = false) {
    return async () => {
        try {
            const {
                newLeadDetail: {
                    attentendAppoiment,
                },
                appointmentNotes
            } = store.getState().lead;
            if (attentendAppoiment?.activeTab === 'Starting treatment' && !attendanceStarted) {
                await dispatch(attenedAppoimentNotes(practiceId, leadId, () => dispatch(addAppointmentNotes(practiceId, leadId, id, data, handleClose, true))));
                return;
            }
            if (!attentendAppoiment?.callAppointmentNote) {
                dispatch(attenedAppoimentNotes(practiceId, leadId, handleClose, { skipStart: attendanceStarted }));
                return;
            }
            if (appointmentNotes.length === 0) {
                const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${id}/appointment_notes/`, 'post', data);
                if (response?.status === 201) {
                    dispatch(attenedAppoimentNotes(practiceId, leadId, handleClose, { skipStart: attendanceStarted }));
                    dispatch(updateError({ success: true, message: 'Appointment notes added successfully' }));
                    return;
                }
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            } else {
                await dispatch(updateAppointmentNotes(practiceId, leadId, id, appointmentNotes, data, handleClose, attendanceStarted));
            }
            dispatch(updateCompleteStatus(false));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: error.message, success: false, cause: error }));
        }
    };
}

export function getAppointmentNotes(practiceId, leadId, id, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${id}/appointment_notes/`);
            if (response?.status === 200) {
                dispatch(leadActions.appointmentNotesSlice(response.data.results));
            } else {
                const message = response?.data && typeof response.data === 'object'
                    ? Object.entries(response.data).map(([k, v]) => `${k}: ${v}`).join(', ')
                    : response?.data?.detail || `Failed to fetch appointment notes (HTTP ${response?.status || 'timeout'})`;
                handleNext?.({ message, success: false });
                dispatch(leadActions.hasError(message));
            }
        } catch (error) {
            const message = error?.message || 'An error occurred while fetching appointment notes';
            handleNext?.({ message, success: false });
            dispatch(leadActions.hasError(message));
        }
    };
}

export function updateAppointmentNotes(practiceId, leadId, id, appointmentNotes, data, handleClose, attendanceStarted = false) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${id}/appointment_notes/${appointmentNotes[0].id}/`, 'put', data);
            if (response?.status === 200) {
                dispatch(attenedAppoimentNotes(practiceId, leadId, handleClose, { skipStart: attendanceStarted }));
                dispatch(updateError({ success: true, message: 'Appointment notes updated successfully' }));
                return;
            }
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
        } catch (error) {
            const message = error?.message || 'An error occurred while updating appointment notes';
            handleClose({
                message,
                success: false
            });
            dispatch(leadActions.hasError(message));
        }
    };
}

export function ConsultationAttended(handleClose) {
    return async () => {
        try {
            const { newLeadDetail, followLead } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
            const { lead_treatments: leadTreatment, lead_appointment: leadAppoiment } = followLead ?? {};
            dispatch(startLoadingData(true));
            dispatch(setLoader(true));
            if (newLeadDetail?.attentendAppoiment?.activeTab === 'Starting treatment') {
                const selectedId = currentTreatmentDataGlobal?.treatmentData?.id ?? store.getState().lead.tableRowLead?.treatment_id;
                const soleTreatment = leadTreatment?.length === 1 ? leadTreatment[0] : null;
                const treatment = selectedId ? leadTreatment?.find(row => idEq(row.id, selectedId)) : soleTreatment;
                if (!treatment) throw new Error('Select the exact treatment before continuing.');
                dispatch(leadActions.saveTreatment(treatment));
                dispatch(leadActions.saveNewLeadDetail({ lead_treatment: treatment }));
                const captureScope = attendanceCoordinator.getScope();
                const stillSelected = () => {
                    const current = store.getState();
                    const currentId = current.multiTreatment?.currentTreatmentDataGlobal?.treatmentData?.id
                        ?? current.lead.tableRowLead?.treatment_id ?? current.lead.createLead?.lead_treatment?.id;
                    const liveScope = attendanceCoordinator.getScope();
                    return liveScope && captureScope && liveScope.epoch === captureScope.epoch && liveScope.sessionKey === captureScope.sessionKey
                        && idEq(liveScope.practiceId, captureScope.practiceId) && idEq(current.practiceTreatmentList?.practiceId, practiceId)
                        && idEq(current.lead.followLead?.id, followLead.id) && idEq(currentId, treatment.id);
                };
                let receipt;
                const finishCapturedStart = async () => {
                    if (!stillSelected()) return;
                    if (!receipt || !idEq(receipt.practice_id, practiceId) || !idEq(receipt.lead_id, followLead.id)
                        || !idEq(receipt.treatment_id, treatment.id) || receipt.status !== 'TREATMENT_STARTED') {
                        throw new Error('Treatment started, but its confirmed consultation could not be verified. Your notes have not been saved.');
                    }
                    const draft = newLeadDetail.attentendAppoiment;
                    if (receipt.appointment_id == null) {
                        clearAttendanceLoaders();
                        if (draft.callAppointmentNote) {
                            dispatch(updateError({ success: false, message: 'Treatment started without a consultation. Your appointment notes remain unsaved; keep this draft until you can attach them to the correct visit.' }));
                            return;
                        }
                        await handleClose('finish', followLead.id);
                        return;
                    }
                    const base = `/api/v1/practices/${practiceId}/leads/${followLead.id}/appointments/${receipt.appointment_id}/`;
                    const response = await callApi(base);
                    const appointment = response?.data;
                    const returnedTreatment = typeof appointment?.lead_treatment === 'object' ? appointment.lead_treatment?.id : appointment?.lead_treatment;
                    const returnedLead = typeof appointment?.lead === 'object' ? appointment.lead?.id : appointment?.lead;
                    if (response?.status !== 200 || !idEq(appointment?.id, receipt.appointment_id)
                        || !idEq(returnedTreatment, treatment.id) || appointment?.type !== 'CONSULTATION'
                        || appointment?.status !== 'ATTENDED' || (returnedLead != null && !idEq(returnedLead, followLead.id))) {
                        throw new Error('Treatment started, but its confirmed consultation could not be verified. Your notes have not been saved.');
                    }
                    const notes = await callApi(`${base}appointment_notes/`);
                    if (notes?.status !== 200 || !Array.isArray(notes.data?.results)) throw new Error('Treatment started, but consultation notes could not be loaded. Your draft has not been saved.');
                    if (!stillSelected()) return;
                    dispatch(leadActions.saveAppointment(appointment));
                    dispatch(leadActions.appointmentNotesSlice(notes.data.results));
                    maybeRunScopedDepositFlow({ leadId: followLead.id, treatmentId: treatment.id, appointmentId: appointment.id });
                    await dispatch(addAppointmentNotes(practiceId, followLead.id, appointment.id, draft.noteDetail, handleClose, true));
                };
                await dispatch(attenedAppoimentNotes(practiceId, followLead.id, finishCapturedStart,
                    { attendanceReceipt: true, onCaptured: result => { if (!stillSelected()) throw new Error('The selected patient or treatment changed. Reopen the action.'); receipt = result; } }));
                return;
            }
            if (leadTreatment && leadTreatment.length && leadAppoiment && leadAppoiment.length) {
                const selectedId = currentTreatmentDataGlobal?.treatmentData?.id ?? store.getState().lead.tableRowLead?.treatment_id;
                const [onlyTreatment] = leadTreatment.length === 1 ? leadTreatment : [];
                const rawTreatment = selectedId ? leadTreatment.find((row) => idEq(row.id, selectedId)) : onlyTreatment;
                const treatment = rawTreatment;
                if (!treatment) { dispatch(updateError({ success: false, message: 'Select the exact treatment before continuing.' })); clearAttendanceLoaders(); return; }
                const treatmentAppointments = getTreatmentAppointments(leadAppoiment, treatment?.id);
                const appointment = treatmentAppointments.find((el) => ATTENDED_APPOINTMENT_STATUSES.includes(el?.status));
                if (!appointment) {
                    dispatch(updateError({ success: false, message: 'No attended appointment found for the selected treatment.' }));
                    clearAttendanceLoaders();
                    return;
                }
                // This is one of the three call sites the "Starting treatment" +
                // deposit-checkbox flow can resolve to — see
                // maybeRunScopedDepositFlow's comment. `treatment.id` is the same
                // raw LeadTreatment id NewAppointment.jsx stamps into depositData
                // (not the V2-converted id used for API URLs elsewhere).
                maybeRunScopedDepositFlow({ leadId: followLead?.id, treatmentId: treatment?.id, appointmentId: appointment?.id });
                dispatch(leadActions.saveAppointment(appointment));
                dispatch(leadActions.saveTreatment(treatment));
                dispatch(leadActions.saveNewLeadDetail({ lead_treatment: treatment }));
                const { noteDetail } = newLeadDetail?.attentendAppoiment || {};
                if (noteDetail) dispatch(addAppointmentNotes(practiceId, followLead.id, appointment.id, noteDetail, handleClose));
                if (newLeadDetail?.addReminder) {
                    const leadNoteData = {
                        lead: followLead.id,
                        note: 'FOLLOW_UP',
                        treatment: getCurrentTreatmentIdV2(rawTreatment.id)
                    };
                    dispatch(addNotes(leadNoteData, practiceId, handleClose, 'attended'));
                }
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        }
    };
}

export function RecordAttended(handleClose, appointmentSelection) {
    return async () => {
        try {
            const { newLeadDetail: { attentendAppoiment } = {}, followLead } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
            const { lead_treatments: leadTreatment } = followLead ?? {};
            dispatch(startLoadingData(true));
            dispatch(setLoader(true));
            if (leadTreatment && leadTreatment.length) {
                const exact = appointmentSelection && resolveAppointmentOutcomeSelection(practiceId, null, followLead, appointmentSelection);
                if (appointmentSelection && !exact) {
                    dispatch(updateError({ success: false, message: 'The selected appointment is unavailable. Close and reload.' }));
                    clearAttendanceLoaders(); return;
                }
                const selectedId = exact?.leadTreatmentId ?? currentTreatmentDataGlobal?.treatmentData?.id ?? store.getState().lead.tableRowLead?.treatment_id;
                const [onlyTreatment] = leadTreatment.length === 1 ? leadTreatment : [];
                const treatment = selectedId ? leadTreatment.find((row) => idEq(row.id, selectedId)) : onlyTreatment;
                if (!treatment) {
                    dispatch(updateError({ success: false, message: 'Select the exact treatment before recording attendance.' }));
                    clearAttendanceLoaders(); return;
                }
                let appointment = exact?.appointment;
                if (!exact) {
                    const captured = await captureConsultationAttendance({ practiceId, leadId: followLead.id, treatmentId: treatment.id });
                    if (captured.status !== 'success') { clearAttendanceLoaders(); return; }
                    const appointmentId = captured.data?.data?.appointment_id;
                    if (!appointmentId && !attendanceUiEnabledFor(practiceId)) {
                        // Legacy practice (flag off): consultation_attended/ succeeded, so attendance IS
                        // recorded, but the treatment has no ATTENDED consultation to hand back — its only
                        // appointment is cancelled, or it was never booked. Before the 27 Oct release this
                        // flow attached notes to the treatment's most recent appointment; keep that so typed
                        // notes are not lost, and otherwise just finish the popup (29 Oct smoke, lead 193595).
                        const fallback = mostRecentAppointment(getTreatmentAppointments(followLead?.lead_appointment ?? [], treatment.id));
                        dispatch(leadActions.saveTreatment(treatment));
                        dispatch(leadActions.saveNewLeadDetail({ lead_treatment: treatment }));
                        const finish = (...args) => {
                            dispatch(startLoadingData(false));
                            dispatch(setLoader(false));
                            if (handleClose) handleClose(...args);
                        };
                        if (!fallback) {
                            if (attentendAppoiment?.callAppointmentNote) {
                                dispatch(updateError({ success: false, message: 'Attendance recorded, but this treatment has no appointment to attach your notes to. Add them as a lead note.' }));
                            }
                            dispatch(attenedAppoimentNotes(practiceId, followLead.id, finish));
                            return;
                        }
                        dispatch(leadActions.saveAppointment(fallback));
                        dispatch(addAppointmentNotes(practiceId, followLead.id, fallback.id, attentendAppoiment?.noteDetail, finish));
                        return;
                    }
                    if (!appointmentId) throw new Error('Attendance was saved but its consultation was not returned. Refresh this patient before continuing.');
                    const loaded = attendanceResponse(await callApi(`/api/v1/practices/${practiceId}/leads/${followLead.id}/appointments/${appointmentId}/`, 'GET'));
                    if (String(store.getState().practiceTreatmentList?.practiceId) !== String(practiceId) || store.getState().lead.followLead?.id !== followLead.id) { clearAttendanceLoaders(); return; }
                    appointment = loaded.data;
                    if (!idEq(appointment.id, appointmentId) || !idEq(appointment.lead_treatment?.id ?? appointment.lead_treatment, treatment.id)) throw new Error('Attendance was saved. Reload the selected consultation before continuing.');
                }
                dispatch(leadActions.saveAppointment(appointment));
                dispatch(leadActions.saveTreatment(treatment));
                dispatch(leadActions.saveNewLeadDetail({ lead_treatment: treatment }));
                const { noteDetail } = attentendAppoiment || {};
                const appointmentDetail = {
                    contacted_via: appointment.contacted_via,
                    datetime: appointment.datetime,
                    lead_treatment: appointment.lead_treatment,
                    method: appointment.method,
                    note: appointment.note,
                };
                if (appointment?.assignees?.length > 0) {
                    appointmentDetail.assignees = appointment.assignees;
                }
                // Wrap the success callback so the loader always resets before
                // the gate dismisses and the Finish button becomes visible.
                // Spread args so attenedAppoimentNotes can still pass ('finish', leadId)
                // to callers that need it (e.g. NewLead.jsx handleClose).
                const closeAndClearLoader = (...args) => {
                    dispatch(startLoadingData(false));
                    dispatch(setLoader(false));
                    if (handleClose) handleClose(...args);
                };
                const handleApi = (data) => {
                    if (data.success) {
                        const confirmed = data.appointment;
                        const confirmedTreatment = confirmed?.lead_treatment?.id ?? confirmed?.lead_treatment;
                        if (!confirmed?.id || !idEq(confirmedTreatment, treatment.id)) {
                            dispatch(updateError({ success: false, message: 'Attendance was saved. Reload the returned consultation before adding notes or payment.' }));
                            clearAttendanceLoaders(); return;
                        }
                        appointment = confirmed;
                        dispatch(leadActions.saveAppointment(appointment));
                        maybeRunScopedDepositFlow({ leadId: followLead.id, treatmentId: treatment.id, appointmentId: appointment.id });
                        dispatch(addAppointmentNotes(practiceId, followLead.id, appointment.id, noteDetail, closeAndClearLoader));
                    } else {
                        // attendmentsAttended returned non-200 — clear loader so UI unblocks.
                        dispatch(startLoadingData(false));
                        dispatch(setLoader(false));
                    }
                };
                if (exact) await dispatch(appointmentsAttended(practiceId, followLead.id, appointment.id, appointmentDetail, handleApi));
                else handleApi({ success: true, appointment });
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        }
    };
}

export function recordUnAttended(handleClose) {
    return async () => {
        try {
            const { newLeadDetail, followLead } = store.getState().lead;
            const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
            dispatch(startLoadingData(true));
            const { faildAttended, } = newLeadDetail;
            const { lead_treatments: leadTreatment, lead_appointment: leadAppoiment } = followLead ?? {};
            const { practiceId } = store.getState().practiceTreatmentList;
            const statuses = ['FAILED_TO_ATTEND_CONSULTATION', 'FAILED_TO_ATTEND'];
            if (leadTreatment && leadTreatment.length) {
                // Treatment selection priority for multi-treatment leads (mirrors RecordAttended):
                // 1. Explicit selection from currentTreatmentDataGlobal (user clicked a specific treatment)
                // 2. Status-based: CONSULTATION_BOOKED treatment (FTA heuristic)
                // 3. [0] fallback for single-treatment leads only
                const selectedId = currentTreatmentDataGlobal?.treatmentData?.id;
                const treatment = selectedId
                    ? (leadTreatment.find((t) => idEq(t.id, selectedId)) ?? leadTreatment.find((t) => t.status === 'CONSULTATION_BOOKED') ?? leadTreatment[0])
                    : (leadTreatment.find((t) => t.status === 'CONSULTATION_BOOKED') ?? leadTreatment[0]);
                const appointmentDetail = {
                    lead_treatment: treatment.id,
                };
                const bookedAppointment = getTreatmentAppointments(leadAppoiment, treatment?.id)
                    .find((el) => BOOKED_APPOINTMENT_STATUSES.includes(el?.status));
                const { newLeadDetail: { archiveLead }, tableRowLead } = store.getState().lead;
                dispatch(leadActions.saveTreatment(treatment));
                dispatch(leadActions.saveNewLeadDetail({ lead_treatment: treatment }));

                if (!statuses.includes(tableRowLead?.status) && faildAttended.activeTab !== 'Reschedule') {
                    if (!bookedAppointment) {
                        dispatch(updateError({ success: false, message: 'No booked appointment found for the selected treatment.' }));
                        clearAttendanceLoaders();
                        return;
                    }
                    dispatch(FailedToAttendAppointment(appointmentDetail, treatment?.lead, bookedAppointment.id, practiceId, handleClose, faildAttended?.activeTab));
                    return;
                }
                if (faildAttended.activeTab === 'Unable to contact') {
                    dispatch(unableToContact(handleClose));
                    return;
                }
                if (faildAttended.activeTab === 'Archive') {
                    const archiveNote = {
                        ...archiveLead.notes,
                        lead: tableRowLead.lead_id
                    };
                    dispatch(addNotes(archiveNote, practiceId, handleClose));
                    return;
                }
                if (faildAttended.activeTab === 'Reschedule') {
                    dispatch(Reschedule(handleClose));
                }
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
        }
    };
}

export function unableToContact(handleClose, isNoNotes) {
    return async () => {
        try {
            const { newLeadDetail, createLead } = store.getState().lead;
            const { unableToContact } = newLeadDetail;
            const { practiceId } = store.getState().practiceTreatmentList;
            if (unableToContact && unableToContact.noteDetail) {
                const noteDetail = {
                    ...unableToContact.noteDetail,
                    contacted_via: 'PHONE'
                };

                if (!isNoNotes) {
                    dispatch(addNotes(noteDetail, practiceId, handleClose, 'unable'));
                    return;
                }

                if (newLeadDetail.addReminder) {
                    const reminderDetail = {
                        ...newLeadDetail.addReminder,
                        lead_treatment: getCurrentTreatmentIdV2(createLead.lead_treatment ? createLead.lead_treatment.id : 0),
                    };
                    dispatch(addReminder(reminderDetail, practiceId, handleClose, 'unable'));
                    return;
                }
                // dispatch(updateError({ message: 'Note added successfully', success: true }));
                dispatch(updateCompleteStatus(false));
                handleClose('finish', noteDetail?.lead);
                dispatch(startLoadingData(false));
                return;
            }

            if (newLeadDetail.addReminder) {
                const reminderDetail = {
                    ...newLeadDetail.addReminder,
                    lead_treatment: createLead.lead_treatment ? createLead.lead_treatment.id : 0,
                };
                dispatch(addReminder(reminderDetail, practiceId, handleClose, 'finish'));
                return;
            }
            handleClose('finish');
            dispatch(startLoadingData(false));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
        }
    };
}

export function Reschedule(handleClose, appointmentSelection, explicitFormData) {
    return async () => {
        try {
            const { newLeadDetail: { startTreatment: stagedTreatment }, createLead, followLead, tableRowLead } = store.getState().lead;
            const { lead_treatments: leadTreatment } = followLead ?? {};
            const { practiceId } = store.getState().practiceTreatmentList;
            const startTreatment = explicitFormData ?? stagedTreatment;
            if (startTreatment) {
                const exact = appointmentSelection && resolveAppointmentOutcomeSelection(practiceId, null, followLead, appointmentSelection);
                if (appointmentSelection && !exact) throw new Error('The selected appointment is unavailable. Close and reload.');
                const treatment = exact?.treatment ?? leadTreatment[0];
                if (exact) {
                    const data = { ...startTreatment.appointmentDetail, lead: exact.leadId, lead_treatment: exact.leadTreatmentId };
                    if (['FAILED_TO_ATTEND_CONSULTATION', 'FAILED_TO_ATTEND'].includes(exact.appointment.status)) {
                        delete data.id;
                        dispatch(addAppointment(data, practiceId, exact.leadId, handleClose, 'Reschedule'));
                    } else {
                        data.id = exact.appointmentId;
                        dispatch(UpdateAppointment(data, practiceId, handleClose, 'Reschedule'));
                    }
                    return;
                }
                if (JSON.stringify(startTreatment.updatedTreatment.treatment) !== JSON.stringify(leadTreatment[0].treatment) || JSON.stringify(startTreatment.updatedTreatment.custom_price) !== JSON.stringify(leadTreatment[0].price) || JSON.stringify(startTreatment.updatedTreatment.teethAmount) !== JSON.stringify(leadTreatment[0]?.leadtreatment_specification?.teeth_count)) {
                    const updateTreatment = {
                        ...startTreatment.updatedTreatment,
                        lead: treatment?.lead || followLead?.id,
                        id: getCurrentTreatmentIdV2(createLead?.lead_treatment?.id)
                    };
                    dispatch(UpdateTreatmentV2(updateTreatment, practiceId, handleClose, 'RescheduleTreatment'));
                    return;
                }
                if (tableRowLead?.status === 'FAILED_TO_ATTEND_CONSULTATION' || tableRowLead?.status === 'FAILED_TO_ATTEND') {
                    dispatch(addAppointment(startTreatment?.appointmentDetail, practiceId, treatment?.lead || followLead?.id, handleClose, 'Reschedule'));
                    return;
                }
                dispatch(UpdateAppointment(startTreatment?.appointmentDetail, practiceId, handleClose, 'Reschedule'));
                return;
            }
            handleClose('finish');
            dispatch(startLoadingData(false));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
        }
    };
}

export function attenedAppoimentNotes(practiceId, leadId, handleClose, { skipStart = false, attendanceReceipt = false, onCaptured } = {}) {
    return async () => {
        const {
            newLeadDetail: {
                lead_treatment: leadTreatment,
                attentendAppoiment,
                startTreatment,
                archiveLead,
            },
            newLeadDetail,
            createLead
        } = store.getState().lead;
        if (attentendAppoiment?.activeTab === 'Starting treatment' && !skipStart) {
            const treatmentId = getCurrentTreatmentIdV2(createLead?.lead_treatment?.id);
            const assignment = startTreatment?.leadAssignment;
            if (assignment && (
                String(assignment.practiceId) !== String(practiceId)
                || String(assignment.leadId) !== String(leadId)
                || !assignment.treatmentId || String(assignment.treatmentId) !== String(treatmentId)
            )) {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ success: false, message: 'The selected treatment changed. Close and reopen before starting.' }));
                return;
            }
            const finishStart = async (...args) => {
                if (assignment) {
                    if (String(store.getState().practiceTreatmentList?.practiceId) !== String(practiceId)) return;
                    let assigned = false;
                    await dispatch(updateAssignToUser({ assignees: assignment.assignees }, leadId, practiceId,
                        (result) => { assigned = result.success; }));
                    if (!assigned) return;
                }
                await handleClose(...args);
            };
            const treatment = {
                treatment: leadTreatment?.treatment,
                custom_price: leadTreatment?.price
            };
            if (JSON.stringify(startTreatment.updatedTreatment) !== JSON.stringify(treatment)) {
                const updateTreatment = {
                    ...(startTreatment?.updatedTreatment ?? {}),
                    lead: leadId,
                    id: treatmentId
                };
                await dispatch(UpdateTreatmentV2(updateTreatment, practiceId, finishStart, 'finish', { attendanceReceipt, onCaptured }));
                return;
            }
            const startTreatments = {
                ...startTreatment.updatedTreatment,
                lead: leadId,
                id: treatmentId
            };
            await dispatch(StartingTreatment(startTreatments, practiceId, finishStart, 'finish', { attendanceReceipt, onCaptured }));
            dispatch(updateCompleteStatus(false));
            return;
        }
        if (attentendAppoiment?.activeTab === 'Follow Up' && newLeadDetail.addReminder) {
            const reminderDetail = {
                ...newLeadDetail.addReminder,
                lead: createLead.id,
                lead_treatment: getCurrentTreatmentIdV2(createLead?.lead_treatment?.id) || 0,
                lead_appointment: getCurrentLeadAppointmentId(createLead.lead_appointment.id) || 0
            };
            dispatch(addReminder(reminderDetail, practiceId, handleClose));
            return;
        }
        if (attentendAppoiment?.activeTab === 'Treatment complete') {
            dispatch(CompleteTreatment(leadTreatment, leadId, getCurrentTreatmentIdV2(createLead?.lead_treatment?.id), practiceId, handleClose));
            return;
        }
        if (attentendAppoiment?.activeTab === 'Archive') {
            const archiveNote = {
                ...archiveLead.notes,
                lead: leadId
            };
            dispatch(addNotes(archiveNote, practiceId, handleClose));
            return;
        }
        dispatch(updateCompleteStatus(false));
        dispatch(updateError({ message: 'Treatment and Appointment updated successfully', success: true }));
        handleClose('finish', leadId);
        dispatch(startLoadingData(false));
    };
}

export function cancelAppointment(practiceId, leadId, id, handleSuccess, dentallySummary = null, requestScopeRef = null) {
    return async () => {
        const capturedScope = captureAppointmentContext(requestScopeRef, practiceId, leadId);
        try {
            const response = dentallySummary
                ? await cancelAppointmentWithDentally(practiceId, leadId, id)
                : await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${id}/cancelled/`, 'post');
            if (!isAppointmentContextCurrent(requestScopeRef, capturedScope)) return;
            const pending = response?.status === 202 || response?.data?.outcome === 'in_progress';
            if (response?.ok || response?.status === 200 || pending) {
                handleSuccess?.({
                    success: true,
                    complete: !pending,
                    pending,
                    message: pending
                        ? 'Appointment cancellation is in progress'
                        : 'Appointment cancelled successfully',
                });
                if (!pending) {
                    dispatch(getAppointment(practiceId, leadId, requestScopeRef));
                    return;
                }

                const pollCancellation = async (attempt) => {
                    if (attempt >= MAX_CANCELLATION_POLL_ATTEMPTS) {
                        if (isAppointmentContextCurrent(requestScopeRef, capturedScope)) {
                            handleSuccess?.({
                                success: false,
                                complete: false,
                                pending: true,
                                refreshAvailable: true,
                                message: 'Cancellation is still in progress. Refresh its status.',
                            });
                        }
                        return;
                    }
                    const shouldContinue = await waitForAppointmentScope(
                        cancellationPollDelay(response?.data?.retry_after_seconds, attempt),
                        requestScopeRef,
                        capturedScope,
                    );
                    if (!shouldContinue) return;
                    const refreshed = await getAppointment(practiceId, leadId, requestScopeRef)();
                    if (!isAppointmentContextCurrent(requestScopeRef, capturedScope)) return;
                    const appointment = refreshed?.status === 200
                        ? refreshed.data?.results?.find((item) => String(item.id) === String(id))
                        : null;
                    if (!appointment) {
                        await pollCancellation(attempt + 1);
                        return;
                    }

                    const cancellationState = appointment.dentally?.cancellation_state;
                    if (appointment.calcom && ['failed', 'unknown', 'unlinked'].includes(appointment.calcom.sync_status)) {
                        handleSuccess?.({ success: false, complete: false, pending: false, refreshAvailable: true,
                            message: appointment.calcom.last_error || 'Cal.com cancellation needs review. Refresh its status before trying again.' });
                        return;
                    }
                    if (appointment.status === 'CANCELLED' || cancellationState === 'CANCELLED') {
                        handleSuccess?.({
                            success: true,
                            complete: true,
                            pending: false,
                            message: 'Appointment cancellation completed',
                        });
                        return;
                    }
                    if (appointment.dentally?.reconciliation_required || cancellationState === 'UNKNOWN') {
                        handleSuccess?.({
                            success: false,
                            complete: false,
                            pending: false,
                            message: 'Appointment cancellation needs review',
                        });
                        return;
                    }
                    if (cancellationState === 'PENDING' || cancellationState === 'ACTIVE') {
                        handleSuccess?.({
                            success: false,
                            complete: false,
                            pending: false,
                            retryable: true,
                            refreshAvailable: true,
                            message: 'Cancellation is not complete. Retry or refresh its status.',
                        });
                        return;
                    }
                    await pollCancellation(attempt + 1);
                };
                await pollCancellation(0);
            } else {
                const message = response?.migrationRequired
                    ? 'This legacy Dentally appointment needs migration before it can be cancelled safely.'
                    : response?.data?.detail || response?.data?.message || 'Appointment cancellation failed';
                const retryable = response?.data?.outcome === 'dentally_pending' && response?.data?.retryable;
                handleSuccess?.({
                    success: false,
                    complete: false,
                    pending: false,
                    retryable,
                    refreshAvailable: retryable,
                    message,
                });
                if (retryable) dispatch(getAppointment(practiceId, leadId, requestScopeRef));
            }
        } catch (error) {
            if (!isAppointmentContextCurrent(requestScopeRef, capturedScope)) return;
            const message = error?.message || 'Something went wrong';
            handleSuccess?.({
                success: false,
                complete: false,
                pending: true,
                refreshAvailable: true,
                ambiguous: true,
                message,
            });
            dispatch(updateError({ message, success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function updateAppointmentDetail(practiceId, leadId, id, detail, handleSuccess) {
    return async () => {
        const requestScope = createEditorReadScope(practiceId, leadId);
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${id}/`, 'patch', withAssigneeIds(detail));
            if (!requestScope.current.active) return;
            if (response?.status === 202 && response?.data?.calcom) {
                dispatch(getAppointment(practiceId, leadId, requestScope));
                dispatch(updateError({ severity: 'warning', message: 'Cal.com change queued. The appointment updates after confirmation.' }));
            } else if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Appointment updated successfully' });
            } else if (isAppointmentDateLocked(response)) {
                // §5 date lock: the date did not change server-side; the cell
                // must drop its optimistic value and say why.
                handleSuccess({ success: false, dateLocked: true, message: appointmentDateLockMessage(response) });
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            if (!requestScope.current.active) return;
            handleSuccess({ success: false, message: error.message || 'Something went wrong' });
            dispatch(leadActions.hasError(error.message));
        }
    };
}

// -----------------------------------------------------------------------------
// revertAttended  (Phase 5.3 — undo attendance)
//
// Attempts to revert a lead treatment from CONSULTATION_ATTENDED back to
// CONSULTATION_BOOKED.
//
// The BE endpoint is speculative (`/api/v1/lead-treatments/{id}/revert_attended/`).
// If the BE returns 404 or 501 (not yet implemented), we gracefully fall back
// with a toast instead of throwing.
//
// @param {number|string} leadTreatmentId — the lead_treatment.id to revert.
// @param {function}      [callback]      — called on success.
// -----------------------------------------------------------------------------
export function revertAttended(leadTreatmentId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/lead-treatments/${leadTreatmentId}/revert_attended/`,
                'post',
                {}
            );
            if (response?.status === 200 || response?.status === 201) {
                callback?.();
            } else if (response?.status === 404 || response?.status === 501) {
                dispatch(updateError({
                    message: 'Undo not available — please change status manually.',
                    success: false,
                }));
            } else {
                dispatch(updateError({
                    message: response?.data?.detail || 'Failed to undo attendance.',
                    success: false,
                }));
            }
        } catch (error) {
            dispatch(updateError({
                message: 'Undo not available — please change status manually.',
                success: false,
                cause: error,
            }));
        }
    };
}

export function updateTransitionTime(data, practiceId, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/treatments/${data.id}/update_transition_time/`, 'patch', data);
            if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Transition time updated successfully' });
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
            dispatch(startLoadingData(false));
        }
    };
}
